import { billingLine } from '../../../../../../models/entities/common_subentities/billingLine';
import { businessPartner } from '../../../../../../models/entities/businessPartner/businessPartner';
import { shipment } from '../../../../../../models/entities/shipment/shipment';
import { bpAccountingResponse } from '../../../../../../models/entities/businessPartner/accounting';

export type BpInvoiceIssuer = {
    name: string;
    address?: string;
    email?: string;
    phone?: string;
    vatNumber?: string;
    /** Tenant company logo URL when available; used on invoice letterhead. */
    logoUrl?: string;
};

/** Build PDF issuer from the logged-in tenant company (singleCompany). */
export function buildBpInvoiceIssuer(company?: {
    name?: string;
    address?: string;
    phoneNumber?: string;
    vatNumber?: number | string;
    logoUrl?: string;
} | null): BpInvoiceIssuer {
    return {
        name: company?.name?.trim() || 'Cargozone',
        address: company?.address,
        // MEDIUM: falls back to platform billing email; company.email not wired yet.
        email: process.env.REACT_APP_BILLING_EMAIL || 'billing@cargozone.ai',
        phone: company?.phoneNumber,
        vatNumber: company?.vatNumber != null ? String(company.vatNumber) : undefined,
        logoUrl: company?.logoUrl
    };
}

export type BpInvoicePdfInput = {
    businessPartner: businessPartner;
    shipment: shipment;
    billingLines: billingLine[];
    issuer: BpInvoiceIssuer;
    invoiceNumber: string;
    invoiceDate: Date;
    dueDate: Date | null;
    paymentTermsLabel: string | null;
    currency: string;
    serviceTypeLabel?: (code: string) => string;
    documentKind?: 'invoice' | 'credit_note';
};

export type BpStatementPdfInput = {
    businessPartner: businessPartner;
    accounting: bpAccountingResponse;
    issuer: BpInvoiceIssuer;
    statementDate: Date;
    accountNumber: string;
};

export const PAYMENT_TERMS_LABELS: Record<string, string> = {
    CASH: 'Cash',
    NET_30: 'Net 30',
    NET_60: 'Net 60',
    NET_90: 'Net 90'
};

export function paymentTermsLabelFromCode(code: string | null | undefined): string | null {
    if (!code) return null;
    return PAYMENT_TERMS_LABELS[code.toUpperCase()] ?? code;
}

export function paymentTermsDays(code: string | null | undefined): number | null {
    const key = (code || '').toUpperCase();
    if (key === 'CASH') return 0;
    if (key === 'NET_30') return 30;
    if (key === 'NET_60') return 60;
    if (key === 'NET_90') return 90;
    return null;
}

export function buildInvoiceNumber(shipmentNumber: string): string {
    return `INV-${shipmentNumber}`;
}

export function parseShipmentNumberFromInvoice(invoiceNumber: string): string | null {
    const match = /^INV-(.+)$/i.exec(invoiceNumber.trim());
    return match?.[1]?.trim() || null;
}

export function isInvalidCargoZoneNumber(value: string | null | undefined): boolean {
    if (!value) return true;
    return /NaN/i.test(value);
}

/** Resolve CZ number for PDF/list when virtual field or partial API payload is incomplete. */
export function resolveShipmentDisplayNumber(input: {
    cargoZoneNumber?: string | null;
    shipmentId?: number | null;
    invoiceNumber?: string;
    accountingShipmentNumber?: string | null;
}): string {
    if (input.accountingShipmentNumber && !isInvalidCargoZoneNumber(input.accountingShipmentNumber)) {
        return input.accountingShipmentNumber;
    }
    if (input.cargoZoneNumber && !isInvalidCargoZoneNumber(input.cargoZoneNumber)) {
        return input.cargoZoneNumber;
    }
    const fromInvoice = input.invoiceNumber ? parseShipmentNumberFromInvoice(input.invoiceNumber) : null;
    if (fromInvoice) return fromInvoice;
    if (input.shipmentId != null && Number.isFinite(Number(input.shipmentId))) {
        return `CZ${String(input.shipmentId).padStart(6, '0')}`;
    }
    return '—';
}
